import React, { useState } from 'react';

import LeftArrowIcon from 'icons/left-arrow-lg.svg';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useFieldAttributesQuery } from 'components/hooks/fieldAttributeQueries';

import ProfilePhotoDisplay from 'components/application/ProfilePhotoDisplay';
import Tabs from 'components/application/Tabs';

import ManagementTab from 'components/mobileTrainingProfile/components/personnelDetailsScreen/ManagementTab';
import PersonnelDetailsTab from 'components/mobileTrainingProfile/components/personnelDetailsScreen/PersonnelDetailsTab';
import TeamsTab from 'components/mobileTrainingProfile/components/personnelDetailsScreen/TeamsTab';
import {
  filterIncluded,
  formatResources,
} from 'components/mobileTrainingProfile/helpers/requests';
import { usePersonnel } from 'components/mobileTrainingProfile/hooks/reactQuery';

import { sortRolesByPrimaryAndPosition } from 'components/helpers/personnel';
import { personDisplayName } from 'components/helpers/users';

export default function PersonnelDetailsScreen({
  onViewHomeClick,
  personnelId,
}) {
  const { t: i18nTCommon } = useTranslation('common');
  const [activeTab, setActiveTab] = useState('personnelDetails');

  const {
    data: personnelQuery,
    isError: isPersonnelQueryError,
    isLoading: isPersonnelQueryLoading,
  } = usePersonnel({ personnelId, select: personnelQuerySelector });

  const {
    data: domainFieldAttributes,
    isSuccess: isDomainFieldAttributesSuccess,
  } = useFieldAttributesQuery();

  if (
    isPersonnelQueryLoading ||
    isPersonnelQueryError ||
    !isDomainFieldAttributesSuccess
  ) {
    return null;
  }

  const personnel = personnelQuery.personnel.indexedById[personnelId];
  const profilePhoto =
    personnelQuery.profilePhotos.indexedById[
      personnel.relationships.profilePhoto.data?.id
    ];
  const lineManager =
    personnelQuery.lineManagers.indexedById[
      personnel.relationships.lineManager.data?.id
    ];
  const company =
    personnelQuery.companies.indexedById[
      personnel.relationships.company.data.id
    ];
  const personnelCompanyRoles = sortRolesByPrimaryAndPosition(
    personnelQuery.userCompanyRoles.resources,
    personnelQuery.companyRoles.resources,
  );

  return (
    <>
      <div className='tw-sticky tw-top-0 tw-z-[999] tw-flex tw-h-20 tw-w-full tw-items-center tw-bg-grey-900 tw-p-3'>
        <div className='tw-flex tw-h-full tw-w-13 tw-flex-none tw-items-center tw-border-0 tw-border-r-1 tw-border-solid tw-border-grey-500'>
          <LeftArrowIcon
            className='tw-ml-0.5 tw-cursor-pointer'
            data-element-name='navigate-home-icon'
            height={40}
            onClick={onViewHomeClick}
            width={40}
          />
        </div>
        <div className='tw-ml-4 tw-mr-4 tw-flex-auto tw-text-grey-025'>
          <h1 className='tw-m-0 tw-font-mark-pro tw-text-xl tw-font-bold tw-tracking-tight'>
            {i18nTCommon('personnel.personnelDetails')}
          </h1>
        </div>
      </div>
      <div className='tw-p-3'>
        <div className='tw-flex tw-flex-col tw-items-center tw-rounded-lg tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-px-4 tw-py-8'>
          <div className='tw-flex tw-items-center tw-justify-center'>
            <div className='tw-flex tw-h-20 tw-w-20 tw-items-center tw-justify-center tw-rounded-full tw-bg-grey-600'>
              <ProfilePhotoDisplay
                componentSize='lg'
                photoUrl={profilePhoto?.links.croppedUrl}
                profileInitials={`${personnel.attributes.firstName[0]}${personnel.attributes.lastName[0]}`}
              />
            </div>
          </div>
          <h2 className='tw-m-0 tw-mt-6 tw-text-2xl tw-font-semibold tw-tracking-tighter tw-text-grey-900'>
            {personDisplayName({ attributes: personnel.attributes })}
          </h2>
          <p className='tw-m-0 tw-text-l'>{company.attributes.name}</p>
          {personnelCompanyRoles.length > 0 && (
            <ul className='tw-m-0 tw-mt-6 tw-list-none tw-p-0 tw-text-center'>
              {personnelCompanyRoles.map((companyRole) => (
                <li className='tw-mb-2 last:tw-mb-0' key={companyRole.id}>
                  <span className='tw-inline-block tw-break-words tw-rounded-[16px] tw-bg-grey-050 tw-px-4 tw-py-1.5 tw-text-center'>
                    {companyRole.attributes.position}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className='tw-mt-6'>
          <Tabs
            activeTab={activeTab}
            modifiers={['truncated']}
            navClassName='tw-overflow-y-hidden tw-whitespace-nowrap [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] tw-h-[22px] tw-mb-3'
            onTabClick={setActiveTab}
          >
            <PersonnelDetailsTab
              defaultDetails={[
                {
                  label: i18nTCommon('personnel.personnelId'),
                  text: personnel.attributes.externalId,
                },
                {
                  label: i18nTCommon('shared.email'),
                  text: personnel.attributes.email,
                },
              ]}
              domainFieldAttributes={domainFieldAttributes}
              fieldValues={personnelQuery.fieldValues.resources}
              id='personnelDetails'
              key='personnelDetailsTab'
              label={i18nTCommon('personnel.personnelDetails')}
            />
            <TeamsTab
              id='teams'
              key='teamsTab'
              label={i18nTCommon('shared.teams')}
              teams={personnelQuery.teams.resources}
            />
            <ManagementTab
              id='management'
              key='managementTab'
              label={i18nTCommon('shared.management')}
              lineManager={lineManager}
              personnelId={personnelId}
            />
          </Tabs>
        </div>
      </div>
    </>
  );
}

PersonnelDetailsScreen.propTypes = {
  personnelId: PropTypes.string.isRequired,
  onViewHomeClick: PropTypes.func.isRequired,
};

const personnelQuerySelector = (response) => {
  const { data } = response;
  const { included } = response;

  return {
    personnel: formatResources({
      resources: [data],
      options: { indexedById: { path: 'id' } },
    }),
    profilePhotos: formatResources({
      resources: filterIncluded({ included, type: 'profilePhoto' }),
      options: { indexedById: { path: 'id' } },
    }),
    companies: formatResources({
      resources: filterIncluded({ included, type: 'company' }),
      options: { indexedById: { path: 'id' } },
    }),
    lineManagers: formatResources({
      resources: filterIncluded({ included, type: 'lineManager' }),
      options: { indexedById: { path: 'id' } },
    }),
    userCompanyRoles: formatResources({
      resources: filterIncluded({ included, type: 'userCompanyRole' }),
      options: { resources: true },
    }),
    companyRoles: formatResources({
      resources: filterIncluded({ included, type: 'companyRole' }),
      options: { resources: true },
    }),
    fieldValues: formatResources({
      resources: filterIncluded({ included, type: 'fieldValue' }),
      options: { resources: true },
    }),
    teams: formatResources({
      resources: filterIncluded({ included, type: 'team' }),
      options: { resources: true },
    }),
  };
};
